import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { eNormalizedEntityType, eNormalizedStatus, eResultState } from '../../enums';
import { Order } from '../../interfaces/orders/order';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { SearchResultsRepositoryService } from '../../services/search-results/search-results-repository.service';
import { concatMap, of, Subject, take, takeUntil, tap } from 'rxjs';
import { Utilities } from '../../utilities';
import { TranslocoService } from '@jsverse/transloco';
import { EnumLabelService } from '../../services/enum-label/enum-label.service';

export interface UboBusinessReportReviewLabels {
  businessName: string;
  legalName: string;
  translatedName: string;
  number: string;
  craNumber: string;
  standardizedType: string;
  registryType: string;
  standardizedStatus: string;
  registryStatus: string;
  homeJurisdiction: string;
  registeredJurisdiction: string;
  annualReturnDue: string;
  lastARFiled: string;
  yearOfLastAR: string;
  annualReturnStatus: string;
  addresses: string;
  noAddresses: string;
  type: string;
  fullAddress: string;
  parties: string;
  noParties: string;
  name: string;
  effectiveDate: string;
  endDate: string;
  civicAddress: string;
}

@Component({
    selector: 'lib-ubo-business-report-review',
    templateUrl: './ubo-business-report-review.component.html',
    styleUrl: './ubo-business-report-review.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UboBusinessReportReviewComponent implements OnInit, OnDestroy {
  private readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  protected results: unknown | null = null;
  public order!: Order;

  private _labels?: Partial<UboBusinessReportReviewLabels>;
  @Input() set labels(value: Partial<UboBusinessReportReviewLabels> | null | undefined) { this._labels = value ?? undefined; }
  /** Caller-provided labels override the translated defaults. */
  get labels(): UboBusinessReportReviewLabels {
    const defaults: UboBusinessReportReviewLabels = {
      businessName: this.transloco.translate('common.labels.businessName2'),
      legalName: this.transloco.translate('businessReportResults.legalName'),
      translatedName: this.transloco.translate('businessReportResults.translatedName'),
      number: this.transloco.translate('common.labels.number2'),
      craNumber: this.transloco.translate('businessReportResults.craNumber'),
      standardizedType: this.transloco.translate('common.labels.standardizedType'),
      registryType: this.transloco.translate('common.labels.registryType'),
      standardizedStatus: this.transloco.translate('businessReportResults.standardizedStatus'),
      registryStatus: this.transloco.translate('businessReportResults.registryStatus'),
      homeJurisdiction: this.transloco.translate('businessReportResults.homeJurisdiction'),
      registeredJurisdiction: this.transloco.translate('businessReportResults.registeredJurisdiction'),
      annualReturnDue: this.transloco.translate('uboOverview.annualReturnDue'),
      lastARFiled: this.transloco.translate('uboOverview.lastArFiled'),
      yearOfLastAR: this.transloco.translate('uboOverview.yearOfLastAr'),
      annualReturnStatus: this.transloco.translate('businessReportResults.annualReturnStatus'),
      addresses: this.transloco.translate('businessReportResults.addresses'),
      noAddresses: this.transloco.translate('uboBusinessReportReview.noAddresses'),
      type: this.transloco.translate('businessSearchResultPreview.type'),
      fullAddress: this.transloco.translate('uboBusinessReportReview.fullAddress'),
      parties: this.transloco.translate('common.labels.parties'),
      noParties: this.transloco.translate('uboBusinessReportReview.noParties'),
      name: this.transloco.translate('common.labels.name2'),
      effectiveDate: this.transloco.translate('businessReportResults.effectiveDate'),
      endDate: this.transloco.translate('businessReportResults.endDate'),
      civicAddress: this.transloco.translate('uboBusinessReportReview.civicAddress'),
    };
    return { ...defaults, ...this._labels } as UboBusinessReportReviewLabels;
  }

  // Map of normalized party type values to translated labels
  // Keys should match the normalizedPartyType values from the API (e.g., "Director", "Officer", "Agent")
  @Input() partyTypeLabels: Record<string, string> = {};

  // Map of normalized address type values to translated labels
  // Keys should match the normalizedAddressType values from the API (e.g., "RegisteredAddress", "MailAddress")
  @Input() addressTypeLabels: Record<string, string> = {};

  // Map of annual return status values to translated labels
  // Keys should match the annualReturnStatus values from the API (e.g., "Overdue", "Filed", "Not due", "Due to be filed")
  @Input() annualReturnStatusLabels: Partial<Record<string, string>> = {};

  // Map of normalized status values to translated labels
  // Keys should match the normalizedStatus values from the API (e.g., "Active", "Inactive", "NotProvided")
  @Input() normalizedStatusLabels: Partial<Record<string, string>> = {};

  private onDestroy$ = new Subject<void>();

  constructor(
    private _orderManager: OrderManagerService,
    private _searchResultsService: SearchResultsRepositoryService
  ) { }

  ngOnInit(): void {
    this._orderManager.currentOrder
      .pipe(take(1))
      .subscribe(order => this.init(order));

    this._orderManager.currentOrder.pipe(
      takeUntil(this.onDestroy$),
      tap(order => (this.order = order)),
      concatMap(order => {
        if (order.searchResult?.resultState?.id === eResultState.noResult) {
          return of(null);
        } else {
          return this._searchResultsService.getBusinessReportParsedResultsAsJson(order.id);
        }
      })
    ).subscribe(payload => {
      this.results = payload;
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  protected init(order: Order): void {
    this.order = order;
  }

  // ---------- JSON helpers (unchanged) ----------
  private asRecord(v: unknown): Record<string, unknown> | undefined {
    return (v !== null && typeof v === 'object') ? (v as Record<string, unknown>) : undefined;
  }
  protected get<T = unknown>(path: string): T | undefined {
    const rec = this.asRecord(this.results);
    return rec?.[path] as T;
  }
  protected getFrom<T = unknown>(obj: unknown, key: string): T | string {
    const rec = this.asRecord(obj);
    return rec?.[key] as T ?? "";
  }
  protected getArray<T = unknown>(path: string): T[] {
    const val = this.get(path);
    return Array.isArray(val) ? (val as T[]) : [];
  }
  protected getString(path: string): string | undefined {
    const v = this.get(path);
    return typeof v === 'string' ? v : undefined;
  }
  protected getEnumString(path: string): string | undefined {
    const v = this.get(path);
    return typeof v === 'string' ? v : undefined;
  }
  protected spaceCase(val: string | undefined): string {
    return Utilities.spacePascalCase(val ?? "");
  }

  // ---------- Display helpers (unchanged from your version) ----------
  protected getStatusIcon(): string { /* same as yours */
    const status = this.getEnumString('normalizedStatus') as eNormalizedStatus | undefined;
    switch (status) {
      case eNormalizedStatus.active: return 'check_circle_outline';
      case eNormalizedStatus.inactive: return 'block';
      default: return '';
    }
  }
  protected getStatusIconColor(): { color: string; } {
    const status = this.getEnumString('normalizedStatus') as eNormalizedStatus | undefined;
    switch (status) {
      case eNormalizedStatus.active: return { color: '#5C7F46' };
      case eNormalizedStatus.inactive: return { color: '#FB2C38' };
      default: return { color: 'black' };
    }
  }
  protected getTypeIcon(): string {
    const type = this.getEnumString('normalizedEntityType') as eNormalizedEntityType | undefined;
    switch (type) {
      case eNormalizedEntityType.businessName: return 'domain';
      case eNormalizedEntityType.corporation: return 'finance';
      default: return '';
    }
  }
  protected addressTypeLabel(address: unknown): string {
    const type = this.getFrom<string>(address, 'normalizedAddressType');
    if (!type || type === 'none') return this.transloco.translate('common.labels.address');
    // Check if we have a translation for this address type
    if (this.addressTypeLabels[type]) {
      return this.addressTypeLabels[type];
    }
    return this.enumLabels.label('normalizedAddressType', type);
  }
  protected partyTypeLabel(party: unknown): string {
    const type = this.getFrom<string>(party, 'normalizedPartyType');
    if (!type || type === 'none') return this.transloco.translate('common.labels.none');
    // Check if we have a translation for this party type
    if (this.partyTypeLabels[type]) {
      return this.partyTypeLabels[type];
    }
    return this.enumLabels.label('normalizedPartyType', type);
  }
  protected getCivicAddress(party: unknown): string | undefined {
    const addresses = this.getFrom(party, 'addresses');
    if (Array.isArray(addresses) && addresses.length > 0) {
      const first = addresses[0];
      return typeof this.getFrom(first, 'fullAddress') === 'string'
        ? (this.getFrom(first, 'fullAddress') as string)
        : '';
    }
    return '';
  }
}